/**
 * Weekly digest archive index — /weekly
 *
 * Lists every published digest, newest first. Acts as the canonical
 * "AI tools week-over-week" landing page for SEO + AI citation.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEOHead from '@/components/SEOHead';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WeeklyDigestIndex() {
  const [digests, setDigests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/weekly`)
      .then((res) => { if (mounted) setDigests(res.data.digests || []); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="weekly-index-page">
      <SEOHead
        title="Weekly AI Tool Digest · theailedger.ai"
        description="Every new AI tool added to theailedger.ai, summarised week by week. Grouped by category and updated every Wednesday."
        path="/weekly"
      />

      <div className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#00F0FF] mb-2">Archive</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold mb-3">Weekly digest</h1>
        <p className="text-neutral-400 text-base leading-relaxed max-w-2xl">
          Every Wednesday we round up the new AI tools, partners, and featured platforms added to theailedger.ai in the previous week — grouped by category. Catch up below or <Link to="/subscribe" className="text-[#00F0FF] hover:underline">subscribe</Link> to get it in your inbox.
        </p>

        <div className="mt-10">
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-[#00F0FF]" />
          ) : digests.length === 0 ? (
            <p className="text-sm text-neutral-500">No digests have been published yet — the first one drops on the next Wednesday.</p>
          ) : (
            <ul className="space-y-3">
              {digests.map((d) => (
                <li key={d.slug} data-testid={`weekly-digest-card-${d.slug}`}>
                  <Link to={`/weekly/${d.slug}`} className="block bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] rounded-xl p-5 transition-colors">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-[#00F0FF]">Week {String(d.iso_week).padStart(2, '0')}, {d.iso_year}</div>
                      <div className="text-xs text-neutral-500">{d.fresh_count} new · {(d.categories || []).length} categories</div>
                    </div>
                    <div className="text-base font-semibold text-white">Top AI tools — Week {String(d.iso_week).padStart(2, '0')}</div>
                    <div className="text-xs text-neutral-500 mt-2 truncate">
                      {(d.categories || []).slice(0, 4).join(' · ')}
                      {(d.categories || []).length > 4 && ` · +${d.categories.length - 4} more`}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
